import { useEffect, useState } from 'react';
import {
  FormatCurrencyForDisplay,
  SkeletonBase,
} from '@unified-checkout/design-system';
import {
  ChevronDown,
  ChevronUp,
} from '@unified-checkout/design-system/icons/index';

import type { IBalanceItemProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance/balances';
import { formatAmountWithCurrency } from '@app/dashboard/views/pages/inner_layout/BalanceAndTransfers/utils';
import { useLangStore } from '@app/dashboard/store/lang';

import styles from './balance-item.module.scss';

const BalanceItem = ({
  balance,
  currency,
  isLoading,
  upcomingBalance,
}: IBalanceItemProps) => {
  const [openAccordion, setOpenAccordion] = useState(false);
  const { lang } = useLangStore();

  const hasUpcomingBalance = !!upcomingBalance?.items?.length;
  const shouldRenderAsAccordion =
    !isLoading && balance.renderAsAccordion && hasUpcomingBalance;

  const formattedAmount = (amount: number) => {
    const formattedAmount = formatAmountWithCurrency({
      amount,
      currency,
      lang,
    });

    return FormatCurrencyForDisplay(formattedAmount);
  };

  const openAccordionHandler = () => {
    if (isLoading || !hasUpcomingBalance) return;

    setOpenAccordion((prev) => !prev);
  };

  useEffect(() => {
    if (openAccordion) setOpenAccordion(false);
  }, [isLoading]);

  return (
    <div className={styles['balance-item']}>
      {isLoading ? (
        <div className={styles['balance-item__skeleton']}>
          <div className={styles['balance-item__skeleton-header']}>
            <SkeletonBase width="30%" height="1rem" />
            <SkeletonBase width="60%" height="1.2rem" />
          </div>
          <SkeletonBase width="20%" height="1rem" />
        </div>
      ) : (
        <div className={styles['balance-item__header']}>
          <div className={styles['balance-item__header-content']}>
            <div
              className={styles['balance-item__header-content-dot']}
              data-balance-dot-color={balance.id}
            ></div>
            <div className={styles['balance-item__header-content-text']}>
              <div
                className={styles['balance-item__header-content-text-label']}
                data-balance-header-accordion={shouldRenderAsAccordion}
                onClick={openAccordionHandler}
              >
                <p
                  className={
                    styles['balance-item__header-content-text-label-text']
                  }
                >
                  {balance.label}
                </p>
                {shouldRenderAsAccordion &&
                  (openAccordion ? ChevronUp : ChevronDown)}
              </div>
              <div
                className={
                  styles['balance-item__header-content-text-description']
                }
              >
                <p
                  className={
                    styles['balance-item__header-content-text-description-text']
                  }
                >
                  <>{balance.description}</>
                </p>
                {balance.action}
              </div>
            </div>
          </div>

          <p className={styles['balance-item__header-value']}>
            {formattedAmount(balance.value ?? 0)}
          </p>
        </div>
      )}

      {shouldRenderAsAccordion && openAccordion && (
        <div className={styles['balance-item__children']}>
          {balance.children}
        </div>
      )}
    </div>
  );
};

export default BalanceItem;
